// Worker page section: monitor findings (platform.monitor_findings). Read-only.
// `detail` holds sums and counts, never payload; only its scalar entries render.
import { useEffect, useState } from 'react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import { useWorkerFindings } from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import { QueryState, SectionHeading, SeverityBadge, fmtTs } from '../worker-ui';

const NAME_RE = /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+$/;

function detailSummary(detail: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(detail)) {
    if (v === null || ['string', 'number', 'boolean'].includes(typeof v)) {
      parts.push(`${k}=${String(v).slice(0, 60)}`);
    }
    if (parts.length === 6) break;
  }
  return parts.length ? parts.join(' · ') : '—';
}

function FindingsSection() {
  const [state, setState] = useState<'open' | 'all'>('open');
  const [raw, setRaw] = useState('');
  const [assertion, setAssertion] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      const v = raw.trim();
      setAssertion(v === '' || NAME_RE.test(v) ? v : '');
    }, 300);
    return () => clearTimeout(t);
  }, [raw]);
  const q = useWorkerFindings(state, assertion);
  const rows = q.data?.findings ?? [];
  return (
    <section className="flex flex-col gap-3" aria-label="Findings">
      <SectionHeading
        title="Findings"
        description="Open findings per assertion and subject, across tenants."
      />
      <div className="flex items-center gap-2 flex-wrap">
        {(['open', 'all'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={state === s ? 'emphasis' : 'secondary'}
            aria-pressed={state === s}
            onClick={() => setState(s)}
          >
            {s === 'open' ? 'Open' : 'All'}
          </Button>
        ))}
        <div className="basis-72">
          <Input
            type="text"
            size="sm"
            value={raw}
            maxLength={200}
            placeholder="Filter by assertion name…"
            aria-label="Filter by assertion"
            onChange={(e) => setRaw(e.target.value)}
          />
        </div>
      </div>
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={rows.length === 0}
        emptyTitle={state === 'open' ? 'No open findings' : 'No findings'}
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Assertion</TableHeaderCell>
                <TableHeaderCell>Subject</TableHeaderCell>
                <TableHeaderCell>Tenant</TableHeaderCell>
                <TableHeaderCell>Severity</TableHeaderCell>
                <TableHeaderCell>First seen</TableHeaderCell>
                <TableHeaderCell>Last seen</TableHeaderCell>
                <TableHeaderCell>Resolved</TableHeaderCell>
                <TableHeaderCell>Detail</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.assertion}</TableCell>
                  <TableCell>{f.subjectKey}</TableCell>
                  <TableCell>{f.tenantId ?? 'platform'}</TableCell>
                  <TableCell>
                    <SeverityBadge severity={f.severity} />
                  </TableCell>
                  <TableCell>{fmtTs(f.firstSeenAt)}</TableCell>
                  <TableCell>{fmtTs(f.lastSeenAt)}</TableCell>
                  <TableCell>{fmtTs(f.resolvedAt)}</TableCell>
                  <TableCell>
                    <span className="text-caption text-muted">{detailSummary(f.detail)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'findings',
  order: 50,
  label: 'Findings',
  perm: PERMS.WORKER_READ,
  Component: FindingsSection,
};
